import React, { useEffect, useState } from "react";

import {
  Box,
  Button,
  FormControl,
  Select,
  SelectChangeEvent,
  MenuItem,
  TextField,
} from "@mui/material";
import { MuiColorInput } from "mui-color-input";
import {
  FormatAlignCenter,
  FormatAlignLeft,
  FormatAlignRight,
  FormatBold,
  FormatUnderlined,
} from "@mui/icons-material";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";

import LetterSpacing from "./LetterSpacing";
import LetterCasing from "./LetterCasing";
import { SubtitleStyles } from "../../../../../types";

interface TextStyleProps {
  styles: SubtitleStyles;
  onUpdateStyles: (styles: SubtitleStyles) => void;
}

export default function TextStyle({ styles, onUpdateStyles }: TextStyleProps) {
  const [localStyles, setLocalStyles] = useState(styles);
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout | null>(
    null,
  );

  const debouncedUpdateStyles = (updatedStyles: SubtitleStyles) => {
    if (debounceTimeout) clearTimeout(debounceTimeout);
    const timeout = setTimeout(() => {
      onUpdateStyles(updatedStyles);
    }, 500);
    setDebounceTimeout(timeout);
  };

  const handleFontChange = (e: SelectChangeEvent<string>) => {
    const updatedStyles = { ...localStyles, font: e.target.value as string };
    setLocalStyles(updatedStyles);
    debouncedUpdateStyles(updatedStyles);
  };

  const handleFontSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const updatedStyles = {
      ...localStyles,
      size: parseInt(e.target.value, 10),
    };
    setLocalStyles(updatedStyles);
    debouncedUpdateStyles(updatedStyles);
  };

  const handleColorChange = (color: string) => {
    const updatedStyles = { ...localStyles, color };
    setLocalStyles(updatedStyles);
    debouncedUpdateStyles(updatedStyles);
  };

  useEffect(() => {
    setLocalStyles(styles);
  }, [styles]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 2,
        }}
      >
        <FormControl sx={{ width: "100%" }}>
          <Select
            title="Font"
            key="font"
            size="small"
            value={localStyles.font || "Bebas Neue"}
            onChange={handleFontChange}
            displayEmpty
            sx={{
              fontFamily: localStyles.font || "Bebas Neue",
            }}
          >
            <MenuItem value="Inter" sx={{ fontFamily: "Inter" }}>
              Inter
            </MenuItem>
            <MenuItem value="Montserrat" sx={{ fontFamily: "Montserrat" }}>
              Montserrat
            </MenuItem>
            <MenuItem value="Outfit" sx={{ fontFamily: "Outfit" }}>
              Outfit
            </MenuItem>
            <MenuItem value="Bebas Neue" sx={{ fontFamily: "Bebas Neue" }}>
              Bebas Neue
            </MenuItem>
            <MenuItem value="Oswald" sx={{ fontFamily: "Oswald" }}>
              Oswald
            </MenuItem>
            <MenuItem value="Josefin Sans" sx={{ fontFamily: "Josefin Sans" }}>
              Josefin Sans
            </MenuItem>
            <MenuItem
              value="Playfair Display"
              sx={{ fontFamily: "Playfair Display" }}
            >
              Playfair Display
            </MenuItem>
            <MenuItem value="Arvo" sx={{ fontFamily: "Arvo" }}>
              Arvo
            </MenuItem>
          </Select>
        </FormControl>
        <TextField
          title="Font Size"
          size="small"
          sx={{
            display: "flex",
            flexGrow: 1,
            minWidth: "80px",
          }}
          type="number"
          value={localStyles.size ?? 24}
          onChange={handleFontSizeChange}
          inputProps={{ min: 8, max: 72 }}
        />
        <Box
          sx={{
            display: "flex",
            flexGrow: 1,
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: "4px",
            "&:hover": {
              borderColor: "rgba(255,255,255,0.5)",
              cursor: "pointer",
            },
          }}
        >
          <MuiColorInput
            title="Font Color"
            format="hex"
            value={localStyles.color ?? "black"}
            onChange={handleColorChange}
            size="small"
            sx={{
              "& input": {
                width: 0,
                border: "none",
                paddingRight: 0,
              },
              "& fieldset": {
                display: "none",
              },
              "& button": {
                boxShadow: "none",
                borderRadius: "50%",
                width: 28,
                height: 28,
                marginLeft: "8px",
                border: "2px solid rgba(255,255,255,0.4)",
                "&:hover": {
                  border: "2px solid rgba(255,255,255,0.8)",
                },
              },
              "& .MuiInputBase-root": {
                padding: 0,
                margin: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              width: 38,
              height: 38,
            }}
          />
        </Box>
      </Box>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: "fit-content",
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            border: "1px solid rgba(255,255,255,0.25)",
            borderRadius: "4px",
            padding: 1,
            gap: 2,
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={() => {
              const updatedStyles = {
                ...localStyles,
                bold: !localStyles.bold,
              };
              setLocalStyles(updatedStyles);
              debouncedUpdateStyles(updatedStyles);
            }}
            sx={{
              width: "38px",
              height: "38px",
              minWidth: "38px",
              "& .MuiButton-startIcon": {
                margin: 0,
              },
              "&:hover": {
                color: (theme) => theme.palette.text.primary,
              },
              margin: 0,
              backgroundColor: (theme) =>
                localStyles.bold
                  ? theme.palette.action.selected
                  : "transparent",
              color: (theme) =>
                localStyles.bold
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
            }}
            startIcon={<FormatBold />}
          />
          <Button
            variant="text"
            size="small"
            onClick={() => {
              const updatedStyles = {
                ...localStyles,
                italic: !localStyles.italic,
              };
              setLocalStyles(updatedStyles);
              debouncedUpdateStyles(updatedStyles);
            }}
            sx={{
              width: "38px",
              height: "38px",
              minWidth: "38px",
              padding: 0,
              "& .MuiButton-startIcon": {
                margin: 0,
              },
              "&:hover": {
                color: (theme) => theme.palette.text.primary,
              },
              margin: 0,
              backgroundColor: (theme) =>
                localStyles.italic
                  ? theme.palette.action.selected
                  : "transparent",
              color: (theme) =>
                localStyles.italic
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            startIcon={<FormatItalicIcon sx={{ fontSize: "16px" }} />}
          />

          <Button
            variant="text"
            size="small"
            onClick={() => {
              const updatedStyles = {
                ...localStyles,
                underline: !localStyles.underline,
              };
              setLocalStyles(updatedStyles);
              debouncedUpdateStyles(updatedStyles);
            }}
            sx={{
              width: "38px",
              height: "38px",
              minWidth: "38px",
              padding: 0,
              "& .MuiButton-startIcon": {
                margin: 0,
              },
              "&:hover": {
                color: (theme) => theme.palette.text.primary,
              },
              margin: 0,
              backgroundColor: (theme) =>
                localStyles.underline
                  ? theme.palette.action.selected
                  : "transparent",
              color: (theme) =>
                localStyles.underline
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            startIcon={<FormatUnderlined sx={{ fontSize: "16px" }} />}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: "4px",
              padding: 1,
              gap: 0.5,
              width: "fit-content",
            }}
          >
            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updatedStyles = {
                  ...localStyles,
                  text_align: "left",
                };
                setLocalStyles(updatedStyles);
                debouncedUpdateStyles(updatedStyles);
              }}
              sx={{
                width: "38px",
                height: "38px",
                minWidth: "38px",
                padding: 0,
                "& .MuiButton-startIcon": {
                  margin: 0,
                },
                "&:hover": {
                  color: (theme) => theme.palette.text.primary,
                },
                margin: 0,
                backgroundColor: (theme) =>
                  localStyles.text_align === "left"
                    ? theme.palette.action.selected
                    : "transparent",
                color: (theme) =>
                  localStyles.text_align === "left"
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              startIcon={<FormatAlignLeft sx={{ fontSize: "16px" }} />}
            />

            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updatedStyles = {
                  ...localStyles,
                  text_align: "center",
                };
                setLocalStyles(updatedStyles);
                debouncedUpdateStyles(updatedStyles);
              }}
              sx={{
                width: "38px",
                height: "38px",
                minWidth: "38px",
                padding: 0,
                "& .MuiButton-startIcon": {
                  margin: 0,
                },
                "&:hover": {
                  color: (theme) => theme.palette.text.primary,
                },
                margin: 0,
                backgroundColor: (theme) =>
                  localStyles.text_align === "center"
                    ? theme.palette.action.selected
                    : "transparent",
                color: (theme) =>
                  localStyles.text_align === "center"
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              startIcon={<FormatAlignCenter sx={{ fontSize: "16px" }} />}
            />

            <Button
              variant="text"
              size="small"
              onClick={() => {
                const updatedStyles = {
                  ...localStyles,
                  text_align: "right",
                };
                setLocalStyles(updatedStyles);
                debouncedUpdateStyles(updatedStyles);
              }}
              sx={{
                width: "38px",
                height: "38px",
                minWidth: "38px",
                padding: 0,
                "& .MuiButton-startIcon": {
                  margin: 0,
                },
                "&:hover": {
                  color: (theme) => theme.palette.text.primary,
                },
                margin: 0,
                backgroundColor: (theme) =>
                  localStyles.text_align === "right"
                    ? theme.palette.action.selected
                    : "transparent",
                color: (theme) =>
                  localStyles.text_align === "right"
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              startIcon={<FormatAlignRight sx={{ fontSize: "16px" }} />}
            />
          </Box>
          <Box
            sx={{
              display: "flex",
              width: "fit-content",
              flexDirection: "row",
              gap: 1,
            }}
          >
            <LetterCasing
              styles={localStyles}
              onUpdateStyles={(updatedStyles) => {
                setLocalStyles(updatedStyles);
                debouncedUpdateStyles(updatedStyles);
              }}
            />

            <LetterSpacing
              styles={localStyles}
              onUpdateStyles={(updatedStyles) => {
                setLocalStyles(updatedStyles);
                debouncedUpdateStyles(updatedStyles);
              }}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
